import type { AnnualTimetableCell } from '../types/domain';
import { buildAnnualCellKey } from './annualTimetable';
import { trackNameFromProgramScopeKey } from './trackScheduleContentRematch';

export type GradeTermScope = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
};

/**
 * 연간 저장본에서 과목명·색이 모두 비어 있는 칸 = 해당 슬롯에 더 이상 시간표(과목) 표시가 없음
 * (공휴일·학기 외 등으로 비운 저장 결과와 동일한 형태)
 */
export function collectClearedAnnualKeysByScopeFromSavedRows(
  savedRows: AnnualTimetableCell[]
): Map<string, Set<string>> {
  const emptyAnnualKeysByScope = new Map<string, Set<string>>();
  for (const row of savedRows) {
    const tv = (row.text_value ?? '').trim();
    const ch = (row.color_hex ?? '').trim();
    if (tv || ch) continue;
    const scopeKey = `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}`;
    const annualKey = buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index);
    if (!emptyAnnualKeysByScope.has(scopeKey)) emptyAnnualKeysByScope.set(scopeKey, new Set());
    emptyAnnualKeysByScope.get(scopeKey)!.add(annualKey);
  }
  return emptyAnnualKeysByScope;
}

export function parseGradeTermScopeKey(scopeKey: string): GradeTermScope | null {
  const [workspaceId, ayStr, tnStr, gradeId] = scopeKey.split('|');
  if (!workspaceId || !gradeId || !ayStr || !tnStr) return null;
  const academicYear = Number(ayStr);
  const termNumber = Number(tnStr) as 1 | 2;
  if (!Number.isInteger(academicYear) || (termNumber !== 1 && termNumber !== 2)) return null;
  return { workspaceId, academicYear, termNumber, gradeId };
}

export type OccupiedAnnualSlot = {
  annualKey: string;
  currentTrackName: string;
};

/**
 * 과목명·색이 하나라도 있는 저장 칸(비우기 삭제가 아닌 변경·유지).
 * `currentTrackName`은 시간표명에서 해석한 편제(해석 불가면 빈 문자열).
 */
export function collectOccupiedAnnualSlotsByScopeFromSavedRows(
  savedRows: AnnualTimetableCell[],
  resolveTrackName: (gradeId: string, timetableText: string) => string
): Map<string, OccupiedAnnualSlot[]> {
  const occupiedByScope = new Map<string, OccupiedAnnualSlot[]>();
  for (const row of savedRows) {
    const tv = (row.text_value ?? '').trim();
    const ch = (row.color_hex ?? '').trim();
    if (!tv && !ch) continue;
    const scopeKey = `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}`;
    const annualKey = buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index);
    const currentTrackName = tv ? resolveTrackName(row.grade_id, tv) : '';
    if (!occupiedByScope.has(scopeKey)) occupiedByScope.set(scopeKey, []);
    occupiedByScope.get(scopeKey)!.push({ annualKey, currentTrackName });
  }
  return occupiedByScope;
}

/** 칸의 현재 편제가 스코프 편제와 다르거나 편제를 알 수 없으면 연간 칸 연결을 끊는다. */
export function shouldUnlinkTrackScopedRowForAnnualSlot(
  scopeKey: string,
  currentTrackName: string
): boolean {
  const trackName = trackNameFromProgramScopeKey(scopeKey);
  if (!trackName) return true;
  const current = (currentTrackName ?? '').trim();
  return !current || trackName !== current;
}
